import { Lot, Sale } from "types";
import { FullSaleContext } from "../ExecuteInSaleContext";

function disallowIfInvoiced(lot: Lot, sale: Sale) {
  if (lot.buyerInvoiceId || lot.sellerInvoiceId) {
    return { editable: false, reason: `This lot has been invoiced.` };
  }
  return { editable: true };
}

function disallowIfCompleted(lot: Lot, sale: Sale) {
  if (lot.buyerInvoiceId && lot.sellerInvoiceId) {
    return { editable: false, reason: `This lot has been completed.` };
  }
  return { editable: true };
}

function disallowIfMovementInCompleted(lot: Lot, sale: Sale) {
  let movementStatusIn = (lot.attributes.movementStatusIn ?? "").toLowerCase();

  if (movementStatusIn === "success" || movementStatusIn === "warning") {
    return { editable: false };
  }

  return { editable: true };
}

function disallowIfMovementOutCompleted(lot: Lot, sale: Sale) {
  let movementStatusOut = (
    lot.attributes.movementStatusOut ?? ""
  ).toLowerCase();

  if (movementStatusOut === "success" || movementStatusOut === "warning") {
    return { editable: false };
  }

  return { editable: true };
}

function disallowIfMovementInOrOutCompleted(lot: Lot, sale: Sale) {
  let movementStatusIn = (lot.attributes.movementStatusIn ?? "").toLowerCase();

  if (movementStatusIn === "success" || movementStatusIn === "warning") {
    return { editable: false };
  }

  let movementStatusOut = (
    lot.attributes.movementStatusOut ?? ""
  ).toLowerCase();

  if (movementStatusOut === "success" || movementStatusOut === "warning") {
    return { editable: false };
  }

  return { editable: true };
}

// evaluators apply in order and stop at the first match
const evaluators: EditableEvaluatorEntry[] = [
  {
    match: /attributes\.cphNumberOrigin/,
    evaluator: disallowIfMovementInCompleted,
  },
  {
    match: /attributes\.cphNumberDestination/,
    evaluator: disallowIfMovementOutCompleted,
  },
  {
    match: /attributes\.dateOfMovementIn/,
    evaluator: disallowIfMovementInCompleted,
  },
  {
    match: /attributes\.dateOfMovementOut/,
    evaluator: disallowIfMovementOutCompleted,
  },
  {
    match: /attributes\.addressMarket/,
    evaluator: disallowIfMovementInOrOutCompleted,
  },
  {
    match: /attributes\.cphNumberMarket/,
    evaluator: disallowIfMovementInOrOutCompleted,
  },

  {
    match: /attributes\.movementDocumentNumberIn/,
    evaluator: disallowIfMovementInCompleted,
  },
  {
    match: /attributes\.movementDocumentNumberOut/,
    evaluator: disallowIfMovementOutCompleted,
  },

  {
    match: /itemMap\..*\.attributes.@eartag/,
    evaluator: disallowIfMovementInOrOutCompleted,
  },
  {
    match: "sellerCustomerId",
    evaluator: (lot, sale) => {
      if (lot.sellerInvoiceId) {
        return {
          editable: false,
          reason: `This seller has been invoiced.`,
        };
      }
      return { editable: true };
    },
  },
  {
    match: "buyerCustomerId",
    evaluator: (lot, sale) => {
      if (lot.buyerInvoiceId) {
        return { editable: false, reason: `This buyer has been invoiced.` };
      }
      if (lot.saleStatus === "Unsold") {
        return { editable: false, reason: `This lot is marked as unsold.` };
      }
      return { editable: true };
    },
  },
  {
    match: "unitPriceInCents",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "buyerCasual",
    evaluator: (lot, sale) => {
      if (lot.saleStatus === "Unsold") {
        return { editable: false, reason: `This lot is marked as unsold.` };
      }
      return { editable: true };
    },
  },
  {
    match: "currency",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "unitOfSale",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "reservePriceInCents",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "startingPriceInCents",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "saleStatus",
    evaluator: disallowIfInvoiced,
  },
  {
    match: "lotNumber",
    evaluator: disallowIfInvoiced,
  },

  {
    match: "startAt",
    evaluator: (lot, sale) => {
      // if it's a MartEye Timed sale then we can't edit the startAt. It's set to the start of the sale.
      if (sale.marteyeSettings && sale.marteyeSettings.type === "TIMED") {
        return {
          editable: false,
          reason: `This sale is a MartEye Timed sale. The start time is set to the start of the sale.`,
        };
      }

      return disallowIfInvoiced(lot, sale);
    },
  },
  {
    match: "endAt",
    evaluator: (lot, sale) => {
      if (sale.marteyeSettings && sale.marteyeSettings.type === "TIMED") {
        return {
          editable: false,
          reason: `This sale is a MartEye Timed sale. The end time calculated automatically.`,
        };
      }

      return disallowIfInvoiced(lot, sale);
    },
  },

  {
    // likely too heavy will disallow changing eartags for movements etc
    match: /.*/,
    evaluator: disallowIfCompleted,
  },
];

export function determineLotEditableState(
  ctx: FullSaleContext,
  lotIdOrLot: string | Lot,
  fieldPath: string
) {
  let lot =
    typeof lotIdOrLot === "string"
      ? ctx.lots.find((l) => l.id === lotIdOrLot)
      : lotIdOrLot;
  let sale = ctx.sale;

  if (!lot || !sale || !fieldPath) {
    return {
      editable: true,
    };
  }

  for (const entry of evaluators) {
    const predicate = createMatcher(entry.match);
    if (predicate(fieldPath)) {
      return entry.evaluator(lot, sale);
    }
  }
  // Default to editable if no match is found.
  return {
    editable: true,
  };
}

// The evaluator signature stays the same
type EditableEvaluator = (
  lot: Lot,
  sale: Sale
) => {
  editable: boolean;
  reason?: string;
};

// Define a type for simple matchers: either an exact string or a predicate function.
type Matcher = keyof Lot | RegExp | ((fieldPath: string) => boolean);

// Utility function that converts a Matcher into a predicate function
function createMatcher(matcher: Matcher): (path: string) => boolean {
  if (typeof matcher === "string") {
    // For strings we offer a simple mechanism: if it ends with a "*", treat it as a prefix match.
    if (matcher.endsWith("*")) {
      const prefix = matcher.slice(0, -1);
      return (path: string) => path.startsWith(prefix);
    } else {
      return (path: string) => path === matcher;
    }
  }

  if (matcher instanceof RegExp) {
    return (path: string) => matcher.test(path);
  }

  // Otherwise, it's already a predicate function.
  return matcher;
}

interface EditableEvaluatorEntry {
  match: Matcher;
  evaluator: EditableEvaluator;
}
